import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * VENOA buy mode: "Ein Duft" vs "2+1 Bundle" inside the product buy box.
 *
 * - Single mode leaves the native Horizon product form untouched.
 * - The bundle panel has its own styled native <select>s (real Shopify
 *   variant ids as values) and keeps its own state; it is only read while
 *   bundle mode is active, so hidden selects can never be sent.
 * - Adding uses ONE Ajax cart request, then dispatches the theme's own
 *   CartLinesUpdateEvent so drawer, icon and count update like a native add.
 *   (Dwell 4.1.5: events live in '@shopify/events'.)
 * - Prices shown = sum of the bottles minus the cheapest one, which is what
 *   the shop's automatic "2 + 1 Gratis" discount applies. Nothing is
 *   calculated that the cart won't match.
 * - The floating pill (venoa-sticky-cta) shows this panel's price in 2+1
 *   mode – no second state, no second add-to-cart button.
 */
const ERROR_DISPLAY_MS = 8000;
const BUNDLE_MODES = ['bundle'];

const shortTitle = (title) => String(title || '').replace(/\s*No\.\s*\d+\s*$/i, '').trim();

class VenoaBuyMode extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.section = this.closest('.shopify-section');
    this.modeInputs = Array.from(this.querySelectorAll('[data-buymode-input]'));
    this.panels = Array.from(this.querySelectorAll('[data-buymode-panel]')).map((panel) => this.readPanel(panel));
    this.moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
    this.busy = false;

    // The floating pill shows the bundle price plus this panel's unit price
    // (Grundpreis) while 2+1 is chosen.
    this.stickyPrice = this.section?.querySelector('[data-sticky-bundle-price]');
    const bundleUnit = this.panels.find((panel) => panel.mode === 'bundle')?.pricing?.querySelector('.venoa-unit');
    if (this.stickyPrice && bundleUnit && !this.stickyPrice.parentElement.querySelector('.venoa-unit')) {
      this.stickyPrice.after(bundleUnit.cloneNode(true));
    }

    this.modeInputs.forEach((input) => {
      input.addEventListener('change', () => this.setMode(this.currentMode()), { signal });
    });
    this.panels.forEach((panel) => {
      panel.selects.forEach((select) => select.addEventListener('change', () => this.update(panel), { signal }));
      panel.addButton?.addEventListener('click', () => this.addBundle(panel), { signal });
    });

    // Deep links like /products/…#venoa-bundle (hero, homepage bundle) land in bundle mode.
    this.applyHash();
    window.addEventListener('hashchange', () => this.applyHash(), { signal });

    this.panels.forEach((panel) => this.update(panel));
    this.setMode(this.currentMode());
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.section?.removeAttribute('data-venoa-mode');
  }

  readPanel(el) {
    return {
      el,
      mode: el.dataset.buymodePanel,
      slots: Array.from(el.querySelectorAll('[data-bundle-slot]')),
      selects: Array.from(el.querySelectorAll('[data-bundle-select]')),
      summary: el.querySelector('[data-bundle-summary]'),
      pricing: el.querySelector('[data-bundle-pricing]'),
      totalEl: el.querySelector('[data-bundle-total]'),
      compareEl: el.querySelector('[data-bundle-compare]'),
      savingEl: el.querySelector('[data-bundle-saving]'),
      unitEl: el.querySelector('[data-bundle-unit]'),
      addButton: el.querySelector('[data-bundle-add]'),
      errorEl: el.querySelector('[data-bundle-error]'),
      state: null,
    };
  }

  applyHash() {
    const hash = window.location.hash;
    const mode = this.id && hash === `#${this.id}` ? 'bundle' : null;
    if (!mode) return;
    const input = this.modeInputs.find((item) => item.value === mode);
    if (input && !input.checked) {
      input.checked = true;
      this.setMode(mode);
    }
  }

  currentMode() {
    return this.modeInputs.find((input) => input.checked)?.value || 'single';
  }

  setMode(mode) {
    this.mode = mode;
    if (this.section) this.section.dataset.venoaMode = BUNDLE_MODES.includes(mode) ? 'bundle' : 'single';
    if (this.section) this.section.dataset.venoaBundle = mode;
    this.panels.forEach((panel) => {
      panel.el.hidden = panel.mode !== mode;
    });
  }

  /** Reads one panel's selects. */
  selection(panel) {
    return panel.selects.map((select) => {
      const option = select?.selectedOptions?.[0];
      if (!(option instanceof HTMLOptionElement)) return null;
      return {
        id: Number(option.value),
        price: Number(option.dataset.price || 0),
        title: option.dataset.title || option.textContent.trim(),
        scent: option.dataset.scent || 'neutral',
        disabled: option.disabled,
      };
    });
  }

  /** @returns {{complete: boolean, sellable: boolean}} */
  evaluate(panel, selection) {
    const complete = selection.length === panel.slots.length && selection.every((item) => item && !item.disabled);
    return { complete, sellable: complete && selection.every((item) => item.id > 0) };
  }

  formatMoney(cents) {
    const value = cents / 100;
    const withComma = value.toFixed(2).replace('.', ',');
    return this.moneyFormat
      .replace('{{amount_with_comma_separator}}', withComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(value).toString())
      .replace('{{amount_no_decimals}}', Math.round(value).toString())
      .replace('{{amount}}', value.toFixed(2));
  }

  update(panel) {
    const selection = this.selection(panel);
    const state = this.evaluate(panel, selection);
    panel.state = { selection, ...state };

    panel.slots.forEach((slot, index) => {
      slot.dataset.scent = selection[index]?.scent || 'neutral';
    });

    // Klare Zusammenfassung: welche Flakons, welcher ist gratis.
    if (panel.summary) {
      const names = selection.filter(Boolean).map((item) => shortTitle(item.title));
      panel.summary.textContent = names.length ? `${names.length} Flakons: ${names.join(' · ')}` : '';
    }

    if (state.complete) {
      const prices = selection.map((item) => item.price);
      const compare = prices.reduce((sum, price) => sum + price, 0);
      const free = Math.min(...prices);
      const total = compare - free;
      panel.totals = { total };
      if (panel.totalEl) panel.totalEl.textContent = this.formatMoney(total);
      if (panel.compareEl) panel.compareEl.textContent = this.formatMoney(compare);
      if (panel.savingEl) panel.savingEl.textContent = this.formatMoney(free);
      // Floor, like Liquid's integer division, so server and client never disagree by a cent.
      if (panel.unitEl) panel.unitEl.textContent = this.formatMoney(Math.floor(total / prices.length));
      if (this.stickyPrice) this.stickyPrice.textContent = this.formatMoney(total);
    } else {
      panel.totals = null;
    }

    const disabled = !state.sellable || this.busy;
    if (panel.addButton) panel.addButton.disabled = disabled;
  }

  showError(panel, message) {
    if (!panel.errorEl) return;
    panel.errorEl.textContent = message || 'Das Bundle konnte nicht hinzugefügt werden. Bitte versuche es erneut.';
    panel.errorEl.hidden = false;
    clearTimeout(panel.errorTimeout);
    panel.errorTimeout = setTimeout(() => {
      panel.errorEl.hidden = true;
    }, ERROR_DISPLAY_MS);
  }

  setBusy(panel, busy) {
    this.busy = busy;
    if (panel.addButton) {
      panel.addButton.dataset.loading = busy ? 'true' : 'false';
      panel.addButton.setAttribute('aria-busy', busy ? 'true' : 'false');
    }
    this.update(panel);
  }

  async addBundle(panel) {
    if (this.busy || panel.mode !== this.mode) return;
    // Always re-read right before sending – never trust an older state.
    this.update(panel);
    const { selection, sellable } = panel.state;
    if (!sellable) return;

    // Same variant chosen twice/thrice → one line with the matching quantity
    // (that is how Shopify's cart stores it; the discount still applies).
    const quantities = new Map();
    selection.forEach((item) => quantities.set(item.id, (quantities.get(item.id) || 0) + 1));
    const items = Array.from(quantities, ([id, quantity]) => ({ id, quantity }));
    if (!items.length || items.some((item) => !(item.id > 0))) return;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId)
      .filter(Boolean)
      .join(',');

    this.setBusy(panel, true);
    if (panel.errorEl) panel.errorEl.hidden = true;

    // Dwell 4.1.5 listens for the storefront standard event and resolves the
    // attached promise once the cart response is in – same flow as the native
    // product form in assets/product-form.js.
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds }),
      });
      const result = await response.json();

      if (result.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        deferred.reject(new Error(result.message || 'Add to cart failed'));
        this.showError(panel, result.message);
        return;
      }

      const cart = await (await fetch('/cart.js')).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'venoa-buy-mode',
          sourceId: this.id.toString(),
          itemCount: selection.length,
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error('[venoa-buy-mode] add failed', error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.showError(panel);
    } finally {
      this.setBusy(panel, false);
    }
  }
}

if (!customElements.get('venoa-buy-mode')) {
  customElements.define('venoa-buy-mode', VenoaBuyMode);
}
